import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { EmailTwoTone, Send } from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface EmailScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  schedule: any[]; // Using TourDay[] type from parent component
  variables: {
    ARTIST: string;
    VENUE: string;
    VENUE_ADDRESS: string;
    DATE: string;
    CONTACT_NAME: string;
    CONTACT_EMAIL: string;
    CONTACT_PHONE: string;
    [key: string]: string;
  };
}

const EmailScheduleDialog: React.FC<EmailScheduleDialogProps> = ({
  open,
  onClose,
  schedule,
  variables
}) => {
  const [testMode, setTestMode] = useState(true);
  const [testEmail, setTestEmail] = useState(''); // Your email for testing
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  
  const handleSendEmail = async () => {
    try {
      setSending(true);
      setResult(null);
      
      // Prepare email data
      const emailData = {
        schedule,
        variables,
        recipient: testMode ? testEmail : variables.CONTACT_EMAIL,
        recipientName: testMode ? 'Test User' : variables.CONTACT_NAME,
        testMode
      };
      
      // Send to backend
      const response = await axiosInstance.post('/api/email/schedule', emailData);
      
      setResult({
        type: 'success',
        message: `Schedule successfully sent to ${testMode ? testEmail : variables.CONTACT_EMAIL}`
      });
    } catch (error) {
      console.error('Error sending schedule email:', error);
      setResult({
        type: 'error',
        message: 'Failed to send email. Please try again.'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailTwoTone />
          <Typography variant="h6">Email Schedule to Artist</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Schedule Information
          </Typography>
          <Typography>Artist: {variables.ARTIST}</Typography>
          <Typography>Venue: {variables.VENUE}</Typography>
          <Typography>Date: {variables.DATE}</Typography>
          <Typography>Items: {schedule.reduce((sum, day) => sum + day.items.length, 0)}</Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Recipient
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
              />
            }
            label="Send to test email (for testing purposes)"
            sx={{ mb: 2, display: 'block' }}
          />
          
          {testMode ? (
            <TextField
              fullWidth
              label="Test Email Address"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography>
                The email will be sent to the artist: 
                <Typography component="span" fontWeight="bold" sx={{ ml: 1 }}>
                  {variables.CONTACT_NAME} ({variables.CONTACT_EMAIL})
                </Typography>
              </Typography>
            </Box>
          )}
        </Box>
        
        {result && (
          <Alert severity={result.type} sx={{ mt: 2 }}>
            {result.message}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={sending ? <CircularProgress size={20} /> : <Send />}
          onClick={handleSendEmail}
          disabled={sending || (testMode && !testEmail)}
        >
          {sending ? 'Sending...' : 'Send Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailScheduleDialog;